import { TFunction } from 'i18next';
import { ICountriesMarketing } from '../../../models/countries-marketing/countries-marketing';
import { TransitTimeItem, TransitTimeWrapper } from './countries-list.styles';
import { MiddleMileIcon } from '../../parcel-creation/parcel-creation-bottom/parcel-creation-bottom.component';
import { DeliveryMethod } from '../../../models/delivery-options/common';
import { getCountryTranslationKey } from '../../../utils/translations/translations.utils';

const PRIORITY_CODES_CONFIG: Record<string, string[]> = {
  CA: ['CA', 'UA', 'PL', 'IN', 'US', 'GB', 'AE'],
  default: ['UA', 'CA', 'US'],
};

export const getCountryName = (
  country: ICountriesMarketing,
  languageCode: string
) => {
  switch (languageCode) {
    case 'uk':
      return country.name.uk;
    case 'ru':
      return country.name.ru;
    default:
      return country.name.en;
  }
};

export const getRegionTranslation = (
  region: string,
  t: (key: string) => string
) => {
  const normalizedRegion = region.toLowerCase().replace(/\s+/g, '_');
  return t(`main_page_delivery_countries_${normalizedRegion}`);
};

export const sortCountries = (
  countriesMarketing: ICountriesMarketing[],
  countryCode: string,
  languageCode: string,
  selectedRegion: string
) => {
  if (!countriesMarketing) return [];

  const filteredByRegion =
    selectedRegion === 'All'
      ? countriesMarketing
      : countriesMarketing.filter(
          (country) => country.region === selectedRegion
        );

  const priorityCodes =
    PRIORITY_CODES_CONFIG?.[countryCode] ?? PRIORITY_CODES_CONFIG.default;

  const priorityCountries = priorityCodes
    .map((code) => filteredByRegion.find((country) => country.code === code))
    .filter(Boolean) as ICountriesMarketing[];

  const otherCountries = filteredByRegion
    .filter((country) => !priorityCodes.includes(country.code!))
    .sort((a, b) => {
      const aName = getCountryName(a, languageCode).toLowerCase();
      const bName = getCountryName(b, languageCode).toLowerCase();
      return aName.localeCompare(bName);
    });

  return [...priorityCountries, ...otherCountries];
};

export const filterCountries = (
  countries: ICountriesMarketing[],
  searchQuery: string,
  languageCode: string
) =>
  countries.filter((country) => {
    const name = getCountryName(country, languageCode).toLowerCase();
    return name.includes(searchQuery.toLowerCase());
  });

export const getDeliveryInfo = (
  selectedCountry: ICountriesMarketing | null,
  t: TFunction,
  departureCountryCode: string | undefined
) => {
  if (!selectedCountry) return [];

  const {
    transitTime,
    maxWeight,
    dimensionsLimitsAllSides,
    dimensionsLimitAnySize,
    dimensionsLimitLongestSide,
    dimensionsLimitMinMaxRange,
    dimensionsLimitPerimeter,
    dutyFreeCustomsLimit,
    maxValue,
    insurance,
  } = selectedCountry;

  const transitIcons = transitTime
    ? [
        {
          method: 'air',
          value: transitTime.air?.value,
          units: transitTime.air?.units,
          prefix: transitTime.air?.prefix,
        },
        {
          method: 'sea',
          value: transitTime.sea?.value,
          units: transitTime.sea?.units,
          prefix: transitTime.sea?.prefix,
        },
        {
          method: 'expedited',
          value: transitTime.expedited?.value,
          units: transitTime.expedited?.units,
          prefix: transitTime.expedited?.prefix,
        },
        {
          method: 'car',
          value: transitTime.car?.value,
          units: transitTime.car?.units,
          prefix: transitTime.car?.prefix,
        },
      ].filter(({ value }) => value)
    : [];

  const normalizeTranslationKey = (key: string) =>
    key.toLowerCase().replace(/\s+/g, '_');

  return [
    {
      label: `${t('main_page_delivery_countries_transit_time')}:`,
      value:
        transitIcons.length > 0 ? (
          <TransitTimeWrapper>
            {transitIcons.map(({ method, value, units, prefix }) => (
              <TransitTimeItem key={method}>
                <MiddleMileIcon method={method as DeliveryMethod} />
                <span>
                  {`${prefix ? `${t(prefix)} ` : ''}` +
                    `${value} ${t(normalizeTranslationKey(`${units}`))}`}
                </span>
              </TransitTimeItem>
            ))}
          </TransitTimeWrapper>
        ) : null,
    },
    {
      label: `${t('main_page_delivery_countries_max_parcel_weight')}:`,
      value: maxWeight
        ? `${maxWeight.value} ${t(maxWeight.units) || ''}`
        : null,
    },
    {
      label: `${t('main_page_delivery_countries_max_parcel_dimensions')}:`,
      value: (() => {
        if (dimensionsLimitPerimeter) {
          return t('main_page_delivery_countries_max_perimeter', {
            side: dimensionsLimitPerimeter.maxSide,
            unit: t(dimensionsLimitPerimeter.units),
            perimeter: dimensionsLimitPerimeter.perimeter,
            units: t(dimensionsLimitPerimeter.units),
          });
        }

        if (dimensionsLimitLongestSide) {
          return t('main_page_delivery_countries_longest_side', {
            side: dimensionsLimitLongestSide.maxSide,
            units: t(dimensionsLimitLongestSide.units),
          });
        }

        if (dimensionsLimitMinMaxRange) {
          return (
            <>
              {t('main_page_delivery_countries_min_italy', {
                cm: t(dimensionsLimitMinMaxRange.minMetric.units),
                in: t(dimensionsLimitMinMaxRange.minImperial.units),
                minMetric: dimensionsLimitMinMaxRange.minMetric.value,
                minImperial: dimensionsLimitMinMaxRange.minImperial.value,
              })}
              <br />
              {t('main_page_delivery_countries_max_italy', {
                cm: t(dimensionsLimitMinMaxRange.maxMetric.units),
                in: t(dimensionsLimitMinMaxRange.maxImperial.units),
                maxMetric: dimensionsLimitMinMaxRange.maxMetric.value,
                maxImperial: dimensionsLimitMinMaxRange.maxImperial.value,
              })}
            </>
          );
        }

        if (dimensionsLimitAnySize) {
          return t('main_page_delivery_countries_any_size', {
            ...dimensionsLimitAnySize.anySize,
          });
        }

        if (dimensionsLimitsAllSides) {
          return `${dimensionsLimitsAllSides.width} x ${dimensionsLimitsAllSides.height} x ${dimensionsLimitsAllSides.depth} ${
            t(dimensionsLimitsAllSides.units) || dimensionsLimitsAllSides.units
          }`;
        }

        return null;
      })(),
    },
    {
      label: `${t('main_page_delivery_countries_duty_free')}:`,
      value: dutyFreeCustomsLimit || null,
    },
    {
      label: `${t('main_page_delivery_countries_max_parcel_value')}:`,
      value: maxValue || null,
    },
    {
      label: `${t(
        getCountryTranslationKey('insurance', departureCountryCode)
      )}:`,
      value: insurance
        ? t('main_page_delivery_countries_insurance_description', {
            price: insurance.insuredPrice,
            procent: insurance.percent,
          })
        : null,
    },
  ].filter((item) => item.value);
};

export const getCountriesToRender = (
  filteredCountries: ICountriesMarketing[],
  selectedCountry: ICountriesMarketing | null
): ICountriesMarketing[] => {
  if (filteredCountries.length > 0) {
    return filteredCountries;
  }

  if (selectedCountry) {
    return [selectedCountry];
  }

  return [];
};

export function adaptMoreInfoLink(
  url: string | undefined,
  selectedCountryAlias: string,
  isDomestic: boolean
) {
  if (!url) return url;

  const result = url.replace('{country_code}', selectedCountryAlias);
  if (!isDomestic) {
    return result;
  }

  const parts = result.split('/');
  parts.pop();
  parts.push('domestic-shipping');

  return parts.join('/');
}
